import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

// Der Maps-Key wird serverseitig pro Request als data-Attribut am <html>
// ausgegeben (includes/maps-config.php) und erst im Browser gelesen.
function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }

  const key = document.documentElement.getAttribute("data-maps-geocoder-key");
  return typeof key === "string" ? key.trim() : "";
}

function filled(v) {
  if (v === null || v === undefined) {
    return false;
  }

  return String(v).trim() !== "";
}

function validCoordinates(lat, lng) {
  return typeof lat === "number" && typeof lng === "number"
    && isFinite(lat) && isFinite(lng)
    && lat >= -90 && lat <= 90
    && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.reverseRequest = null;
  }

  // Bricht die laufende Suche ab; ihr Promise endet mit null.
  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  cancelReverse() {
    if (this.reverseRequest != null) {
      this.reverseRequest.source.cancel();
      this.reverseRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.name) {
      p.push(g.name);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Altes Antwortschema ohne properties.type (osm_key/osm_value).
  _legacyFields(pr) {
    const f = {};
    let name = pr.name;

    if (pr.osm_key == "highway") {
      if (filled(name)) {
        f.street = name;
      }
      name = null;
    }

    if (filled(pr.street)) {
      f.street = pr.street;
      if (filled(pr.housenumber)) {
        f.number = pr.housenumber;
      }
    }

    if (filled(pr.city)) {
      f.city = pr.city;
      const district = pr.osm_key == "boundary"
        || (pr.osm_key == "place" && pr.osm_value == "suburb");
      if (district && filled(name) && name != pr.city) {
        f.district = name;
      }
    } else if (filled(name) && pr.osm_key == "place") {
      f.city = name;
    } else if (filled(name)) {
      f.name = name;
    }

    return f;
  }

  _geoaddress(l) {
    if (!l || typeof l !== "object") {
      return null;
    }

    const pr = l.properties && typeof l.properties === "object" ? l.properties : {};
    const coordinates = l.geometry && Array.isArray(l.geometry.coordinates)
      ? l.geometry.coordinates
      : [];
    // GeoJSON: [lng, lat]
    const lng = coordinates[0];
    const lat = coordinates[1];

    if (!validCoordinates(lat, lng)) {
      return null;
    }

    let f = {};

    switch (pr.type) {
      case "house":
        f.street = filled(pr.street) ? pr.street : null;
        f.number = filled(pr.street) && filled(pr.housenumber) ? pr.housenumber : null;
        if (!f.street && filled(pr.name)) {
          f.name = pr.name;
        }
        f.city = filled(pr.city) ? pr.city : null;
        f.district = filled(pr.district) && pr.district != pr.city ? pr.district : null;
        break;

      case "street":
        f.street = filled(pr.name) ? pr.name : (filled(pr.street) ? pr.street : null);
        f.city = filled(pr.city) ? pr.city : null;
        f.district = filled(pr.district) && pr.district != pr.city ? pr.district : null;
        break;

      case "city":
        f.city = filled(pr.name) ? pr.name : (filled(pr.city) ? pr.city : null);
        break;

      case "district":
        if (filled(pr.city)) {
          f.city = pr.city;
          if (filled(pr.name) && pr.name != pr.city) {
            f.district = pr.name;
          }
        } else if (filled(pr.name)) {
          f.district = pr.name;
        }
        break;

      case "locality":
      case "county":
      case "state":
      case "country":
      case "other":
        if (filled(pr.name)) {
          f.name = pr.name;
        }
        if (filled(pr.street)) {
          f.street = pr.street;
          if (filled(pr.housenumber)) {
            f.number = pr.housenumber;
          }
        }
        f.city = filled(pr.city) ? pr.city : null;
        f.district = filled(pr.district) && pr.district != pr.city ? pr.district : null;
        break;

      default:
        f = this._legacyFields(pr);
    }

    const geoaddress = {};
    ["street", "number", "city", "district", "name"].forEach(k => {
      if (filled(f[k])) {
        geoaddress[k] = f[k];
      }
    });

    if (filled(pr.postcode)) {
      geoaddress["zip"] = pr.postcode;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = pr.state;
    geoaddress["country"] = pr.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(data) {
    return data && Array.isArray(data.features) ? data.features : [];
  }

  geocode(params) {
    const request = this.axiosRequest;

    return new Promise((resolve, reject) => {
      this.axios.get(SEARCH_URL, {
        cancelToken: request.source.token,
        timeout: REQUEST_TIMEOUT,
        params
      }).then(res => {
        if (this.axiosRequest !== request) {
          resolve(null);
          return;
        }

        const results = [];
        this._features(res.data).forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });

        resolve(results);
      }).catch((thrown) => {
        if (this.axios.isCancel(thrown) || this.axiosRequest !== request) {
          resolve(null);
          return;
        }

        // ohne URL loggen: sie enthält den Maps-Key
        console.log("Geocoding fehlgeschlagen: " + (thrown && thrown.message ? thrown.message : "unbekannter Fehler"));
        reject(thrown);
      }).finally(() => {
        if (this.axiosRequest === request) {
          this.axiosRequest = null;
        }
      });
    });
  }

  // Liefert ein Array mit Treffern, oder null, wenn die Suche abgebrochen,
  // durch eine neuere ersetzt wurde oder kein Maps-Key vorliegt.
  autocomplete(q) {
    this.cancelAutocomplete();

    const key = mapsKey();
    if (!key || !filled(q)) {
      return Promise.resolve(null);
    }

    const CancelToken = this.axios.CancelToken;
    this.axiosRequest = {
      source: CancelToken.source()
    };

    const params = new URLSearchParams();
    params.append("q", String(q).trim());
    params.append("key", key);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("layer", "city");
    params.append("layer", "district");

    return this.geocode(params);
  }

  // Liefert die Adresse zu den Koordinaten, oder null ohne Maps-Key, bei
  // ungültigen Koordinaten oder wenn eine neuere Anfrage sie ersetzt hat.
  reverse(lat, lng) {
    this.cancelReverse();

    const key = mapsKey();
    lat = typeof lat === "string" ? parseFloat(lat) : lat;
    lng = typeof lng === "string" ? parseFloat(lng) : lng;

    if (!key || !validCoordinates(lat, lng)) {
      return Promise.resolve(null);
    }

    const CancelToken = this.axios.CancelToken;
    const request = {
      source: CancelToken.source()
    };
    this.reverseRequest = request;

    return new Promise((resolve, reject) => {
      this.axios.get(REVERSE_URL, {
        cancelToken: request.source.token,
        timeout: REQUEST_TIMEOUT,
        params: {
          lat: lat,
          lon: lng,
          lang: "de",
          key: key
        }
      }).then(res => {
        if (this.reverseRequest !== request) {
          resolve(null);
          return;
        }

        const features = this._features(res.data);
        const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
        if (geoaddress) {
          resolve(geoaddress);
        } else {
          reject(new Error("invalid data"));
        }
      }).catch(err => {
        if (this.axios.isCancel(err) || this.reverseRequest !== request) {
          resolve(null);
          return;
        }

        reject(err);
      }).finally(() => {
        if (this.reverseRequest === request) {
          this.reverseRequest = null;
        }
      });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory, Geocoder };

export default {};
